import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import ApolloError from '@shared/components/ApolloError';
import { MoveLicenseToOrgUnitValidator } from './validators/moveLicenseToOrgUnitValidator';
import { DialogActions, DialogContent } from '@mui/material';
import Asterisk from '@shared/components/Asterisk';
import MOVE_LICENSE_ORGANIZATIONS from '../../../queries/changeLicenseQueries/moveLicenseOrganizations';
import { MOVE_LICENSE_TYPES } from '../../../constants/MoveLicenseTypes';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { useNavigate } from 'react-router';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import FormOrgUnitSelector from '@shared/components/form/FormOrgUnitSelector';
import MotionSection from '@shared/components/animations/MotionSection';
import { AnimatePresence } from 'framer-motion';
import MOVE_USER_LICENSE_TO_ORG_UNIT from '../../../queries/changeLicenseQueries/moveUserLicenseToOrgUnitMutation';
import {
  FORM_ID,
  MOVE_TEXT,
  CANCEL_TEXT,
  SAVE_TEXT,
  USER_MOVED_SUCCESSFULLY_MESSAGE,
  MOVE_CONFIRMATION_MESSAGE,
  FORM_INCOMPLETE_MESSAGE
} from './constants/moveLicenseConstants';

/**
 * @typedef {object} MoveLicenseToOrgUnitProps
 * @property {number} orgId
 * @property {number} orgUnitId
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {string} [previousLink]
 */

/**
 * @param {MoveLicenseToOrgUnitProps} props
 */
const MoveLicenseToOrgUnit = props => {
  const { orgId, orgUnitId, dialogContent, dialogActions, externalUserId, previousLink } = props;

  const form = useForm({
    mode: 'onChange',
    resolver: joiResolver(MoveLicenseToOrgUnitValidator.schema),
    defaultValues: {
      licensedOrgUnitId: null
    }
  });

  const { handleSubmit, trigger } = form;

  const navigate = useNavigate();
  const client = useApolloClient();

  const {
    data: popUpData,
    loading: popUpDataLoading,
    error: popUpDataError
  } = useQuery(MOVE_LICENSE_ORGANIZATIONS, {
    variables: {
      moveLicenseType: MOVE_LICENSE_TYPES.MDDAP_OU,
      orgId: orgId
    }
  });
  const organizations = popUpData?.moveLicenseOrganizations || [];
  const filteredOrgs = organizations.map(org => ({
    ...org,
    MddapOrgUnits: org.MddapOrgUnits.filter(ou => ou.Id !== orgUnitId)
  }));

  const [moveUser, { error: moveUserError }] = useMutation(MOVE_USER_LICENSE_TO_ORG_UNIT, {
    update: () => client.clearStore()
  });

  const handleConfirm = async formData => {
    const result = await moveUser({
      variables: {
        userLicenseInfo: {
          ExternalUserId: externalUserId,
          LicensedOrgUnitId: formData.licensedOrgUnitId
        }
      }
    });

    if (!result.data.moveUserLicenseToOrgUnit.Success) {
      showErrorToast(result.data.moveUserLicenseToOrgUnit.Errors.map(e => e.Message).join(', '));
      return;
    }

    showSuccessToast(USER_MOVED_SUCCESSFULLY_MESSAGE);
    navigate(previousLink);
  };

  const openModalPreCondition = async () => {
    const validationResult = await trigger();

    if (!validationResult) {
      showErrorToast(FORM_INCOMPLETE_MESSAGE);
    }

    return validationResult;
  };

  if (popUpDataError || moveUserError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(handleConfirm)}>
      <FormProvider {...form}>
        <DialogContent>
          {dialogContent}

          <AnimatePresence>
            <MotionSection keyProp='move-license-to-org-unit'>
              <div className='page-section'>
                <h4 className='field-title'>
                  Org Unit
                  <Asterisk />
                </h4>

                <FormOrgUnitSelector
                  organizations={filteredOrgs}
                  loading={popUpDataLoading}
                  multiSelect={false}
                  name='licensedOrgUnitId'
                />
              </div>
            </MotionSection>
          </AnimatePresence>
        </DialogContent>

        <DialogActions>
          {dialogActions}

          <ConfirmDialogWithButton
            openModalBtnText={SAVE_TEXT}
            confirmBtnText={MOVE_TEXT}
            cancelBtnText={CANCEL_TEXT}
            message={MOVE_CONFIRMATION_MESSAGE}
            dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
            formId={FORM_ID}
            openModalPreCondition={openModalPreCondition}
          />
        </DialogActions>
      </FormProvider>
    </form>
  );
};

export default MoveLicenseToOrgUnit;
